﻿import * as React from "react";
import { Box, List, ListItem, ListItemText } from "@material-ui/core";
import axios from "axios";
import { ISearchResult } from "./SearchResults";

interface IReleaseNote {
    applicationID: number;
    releaseID: number;
    applicationName: string;
    brandedName: string;
    publishDate: string;
    version: string;
    notes: INote[];
}

interface INote {
    releaseNoteID: number;
    releaseNoteBody: string;
    releaseNoteTypeID: number;
}

interface INotesProps {
    applicationID: number;
    searchText: string;
    updateSearchResults: (searchResults: ISearchResult[]) => void;
}

export const Notes: React.FunctionComponent<INotesProps> = (
    props,
): React.ReactElement => {
    const [releaseNotes, setNotes] = React.useState<IReleaseNote[]>(null),
        [loading, setLoading] = React.useState(true);

    // Set Notes
    React.useEffect(() => {
        if (props.applicationID === null) {
            return;
        }

        const applicationID = props.applicationID;

        // Get relase notes
        axios
            .get(`${ROOT_URL}/GetReleaseNotes`, {
                params: {
                    applicationID,
                },
            })
            .then((response) => {
                setNotes(response.data);
                setLoading(false);
            })
            .catch((error) => {
                console.log(error);
            });
    }, [props.applicationID]);

    // Search
    React.useEffect(() => {
        updateSearchResults();
    }, [props.searchText, releaseNotes]);

    const updateSearchResults = (): void => {
        if (
            props.searchText.length < 3 ||
            releaseNotes === null ||
            releaseNotes?.length === 0
        ) {
            return;
        }

        const searchWords = props.searchText.toLowerCase().split(` `);

        let searchResults = [] as ISearchResult[];

        for (let a = 0; a < releaseNotes.length; a++) {
            for (let b = 0; b < releaseNotes[a].notes.length; b++) {
                searchResults.push({
                    applicationID: releaseNotes[a].applicationID,
                    brandedName: releaseNotes[a].brandedName,
                    noteText: releaseNotes[a].notes[b].releaseNoteBody,
                    noteTextLower:
                        releaseNotes[a].notes[b].releaseNoteBody.toLowerCase(),
                    publishDate: releaseNotes[a].publishDate,
                    releaseNoteID: releaseNotes[a].notes[b].releaseNoteID,
                    searchWeight: 0,
                    version: releaseNotes[a].version,
                });
            }
        }

        for (let i = 0; i < searchWords.length; i++) {
            // Search note text for search word and increase search weight if found
            searchResults = searchResults.map((searchObj) =>
                searchObj.noteTextLower.includes(searchWords[i])
                    ? {
                          ...searchObj,
                          searchWeight: searchObj.searchWeight + 1,
                      }
                    : searchObj,
            );

            // Search version for search word and increase search weight if found
            searchResults = searchResults.map((searchObj) =>
                searchObj.version.includes(searchWords[i])
                    ? {
                          ...searchObj,
                          searchWeight: searchObj.searchWeight + 1,
                      }
                    : searchObj,
            );
        }

        // Remove results with search weight = 0
        searchResults = searchResults.filter(
            (searchObj) => searchObj.searchWeight > 0,
        );

        // Sort by search weight
        searchResults.sort((a, b) =>
            a.searchWeight < b.searchWeight
                ? 1
                : b.searchWeight < a.searchWeight
                  ? -1
                  : 0,
        );

        props.updateSearchResults(searchResults);
    };

    const getNoteTypeByID = (noteTypeID: number): string => {
        switch (noteTypeID) {
            case 1:
                return `newfeature`;

            case 2:
                return `featurechange`;

            case 3:
                return `bugfix`;
        }
    };

    let legendDisplayed = false,
        noteList;

    if (!loading) {
        noteList = releaseNotes.map((release) => {
            const displayLegend = !legendDisplayed;

            if (displayLegend) {
                legendDisplayed = true;
            }

            return (
                <div key={release.releaseID}>
                    <ListItem>
                        <ListItemText
                            disableTypography={true}
                            primary={
                                displayLegend ? (
                                    <div>
                                        <span
                                            className={`releasenotes-list-primary`}
                                        >
                                            {release.brandedName}
                                        </span>
                                        <span
                                            className={`releasenotes-legend bugfix`}
                                        >
                                            Bug Fix
                                        </span>
                                        <span
                                            className={`releasenotes-legend featurechange`}
                                        >
                                            Feature Change
                                        </span>
                                        <span
                                            className={`releasenotes-legend newfeature`}
                                        >
                                            New Feature
                                        </span>
                                    </div>
                                ) : (
                                    <div
                                        className={`releasenotes-list-primary`}
                                    >
                                        {release.brandedName}
                                    </div>
                                )
                            }
                            secondary={
                                <div className={`releasenotes-list-secondary`}>
                                    <div>{`Published: ${release.publishDate.substr(
                                        0,
                                        10,
                                    )}`}</div>
                                    <div>{`V ${release.version}`}</div>
                                </div>
                            }
                        />
                    </ListItem>
                    <div className={`releasenotes-linebreak`} />
                    {release.notes.map((note) => (
                        <div key={note.releaseNoteID}>
                            <div className={`releasenotes-flexrow`}>
                                <Box
                                    className={`notetype ${getNoteTypeByID(
                                        note.releaseNoteTypeID,
                                    )}`}
                                />
                                <Box>
                                    <ListItem className={`releasenotes-note`}>
                                        <ListItemText
                                            disableTypography={true}
                                            primary={note.releaseNoteBody}
                                        />
                                    </ListItem>
                                </Box>
                            </div>
                            <div className={`releasenotes-linebreak`} />
                        </div>
                    ))}
                </div>
            );
        });
    }

    return <List className={`releasenotes-scrollable`}>{noteList}</List>;
};
